import type { TimestampStyle } from '../markdown/parser.js';
import { t as defaultT, type Translate } from '../i18n/index.js';

const LOCALE = 'pt-BR';

function date(value: string | number | Date): Date {
  return value instanceof Date ? value : new Date(value);
}

function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

const hourFormat = new Intl.DateTimeFormat(LOCALE, {
  hour: '2-digit',
  minute: '2-digit',
});

/** Hora da mensagem como o Discord: "Hoje às 14:03", "Ontem às 09:12", "01/10/2026 14:03". */
export function formatMessageTime(
  value: string | number | Date,
  now: Date = new Date(),
  t: Translate = defaultT,
): string {
  const when = date(value);
  const hora = hourFormat.format(when);
  if (sameDay(when, now)) return t('mensagem.hoje', { hora });
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (sameDay(when, yesterday)) return t('mensagem.ontem', { hora });
  return `${new Intl.DateTimeFormat(LOCALE, { day: '2-digit', month: '2-digit', year: 'numeric' }).format(when)} ${hora}`;
}

/** Só a hora (mensagens agrupadas). */
export function formatShortTime(value: string | number | Date): string {
  return hourFormat.format(date(value));
}

/** Divisória de dia: "1 de outubro de 2026". */
export function formatDayDivider(value: string | number | Date): string {
  return new Intl.DateTimeFormat(LOCALE, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date(value));
}

/** "há 5 minutos", "em 3 dias" (o estilo `R` das datas do Discord). */
export function formatRelative(
  value: string | number | Date,
  now: Date = new Date(),
  t: Translate = defaultT,
): string {
  const diff = date(value).getTime() - now.getTime();
  const seconds = Math.round(Math.abs(diff) / 1000);
  if (seconds < 5) return t('tempo.agora');
  const units: Array<[string, number]> = [
    ['anos', 365 * 24 * 3600],
    ['meses', 30 * 24 * 3600],
    ['dias', 24 * 3600],
    ['horas', 3600],
    ['minutos', 60],
    ['segundos', 1],
  ];
  const [unit, size] = units.find(([, size]) => seconds >= size) ?? units[5];
  const n = Math.floor(seconds / size);
  const duracao = t(`tempo.${unit}`, { n });
  return t(diff < 0 ? 'tempo.ha' : 'tempo.em', { duracao });
}

/** `<t:unix:estilo>` como o Discord mostra, em pt-BR. */
export function formatDiscordTimestamp(
  unix: number,
  style: TimestampStyle | null,
  now: Date = new Date(),
  t: Translate = defaultT,
): string {
  const when = new Date(unix * 1000);
  const options: Record<TimestampStyle, Intl.DateTimeFormatOptions> = {
    t: { hour: '2-digit', minute: '2-digit' },
    T: { hour: '2-digit', minute: '2-digit', second: '2-digit' },
    d: { day: '2-digit', month: '2-digit', year: 'numeric' },
    D: { day: 'numeric', month: 'long', year: 'numeric' },
    f: {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    },
    F: {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    },
    R: {},
  };
  const chosen = style ?? 'f';
  if (chosen === 'R') return formatRelative(when, now, t);
  return new Intl.DateTimeFormat(LOCALE, options[chosen]).format(when);
}

/** Tamanho de arquivo: "980 bytes", "1,5 KB", "12,3 MB". */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} ${bytes === 1 ? 'byte' : 'bytes'}`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit++;
  }
  return `${new Intl.NumberFormat(LOCALE, { maximumFractionDigits: value < 10 ? 1 : 0 }).format(value)} ${units[unit]}`;
}

/** Duração do modo lento ou castigo: "5 s", "2 min", "1 h", "1 dia". */
export function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds} s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)} min`;
  if (seconds < 86_400) return `${Math.round(seconds / 3600)} h`;
  const days = Math.round(seconds / 86_400);
  return `${days} ${days === 1 ? 'dia' : 'dias'}`;
}

/** Cor do cargo (`color` do Discord) em `#rrggbb`. */
export function roleColorHex(color: number): string {
  return `#${color.toString(16).padStart(6, '0')}`;
}

export { systemMessageText } from './mensagens-do-sistema.js';
export { TEXTOS_DA_AUDITORIA } from './auditoria.js';
export {
  fileUrl,
  sizedImageUrl,
  THUMBNAIL_SIZES,
  avatarUrl,
  guildIconUrl,
  emojiUrl,
  stickerUrl,
  eventCoverUrl,
  groupIconUrl,
  memberAvatarUrl,
  bannerUrl,
} from './arquivos.js';
